import {
  MarketComplianceProfile,
  PrivacyRightsRequest,
  FinancialRegulatoryActivity,
  RetentionPolicyItem,
  LegalHoldRecord,
  ComplianceGateState
} from './complianceTypes';
import {
  MARKET_COMPLIANCE_PROFILES,
  PRIVACY_RIGHTS_REQUESTS,
  FINANCIAL_REGULATORY_ACTIVITIES,
  RETENTION_POLICIES,
  LEGAL_HOLDS,
  TRACKING_TECHNOLOGIES_REGISTRY,
  LEGAL_DOCUMENTS
} from './complianceMockData';

class ComplianceEngine {
  private markets: MarketComplianceProfile[] = [...MARKET_COMPLIANCE_PROFILES];
  private dsarRequests: PrivacyRightsRequest[] = [...PRIVACY_RIGHTS_REQUESTS];
  private finActivities: FinancialRegulatoryActivity[] = [...FINANCIAL_REGULATORY_ACTIVITIES];
  private retentionPolicies: RetentionPolicyItem[] = [...RETENTION_POLICIES];
  private legalHolds: LegalHoldRecord[] = [...LEGAL_HOLDS];

  // 1. Market Compliance Launch Gate Check (Interlocks with Prompt 21 Monetization Master Switch)
  public checkMarketPaidEligibility(countryCode: string): {
    eligible: boolean;
    gateState: ComplianceGateState;
    reason: string;
    operatingEntity: string;
  } {
    const market = this.markets.find((m) => m.countryCode.toUpperCase() === countryCode.toUpperCase());
    if (!market) {
      return {
        eligible: false,
        gateState: 'LEGAL_NOT_REVIEWED',
        reason: `Country code ${countryCode} has not been audited or registered in the Legal Authority Registry.`,
        operatingEntity: 'N/A'
      };
    }

    if (market.paidMonetizationGate === 'LEGAL_READY_LIVE') {
      return {
        eligible: true,
        gateState: 'LEGAL_READY_LIVE',
        reason: 'Market is fully certified: Operating entity registered, DPA signed, consumer terms active, and tax treatment verified.',
        operatingEntity: market.operatingEntity
      };
    }

    return {
      eligible: false,
      gateState: market.paidMonetizationGate,
      reason: market.hardBlockReason || `Market gate is ${market.paidMonetizationGate}. Commercial checkout is strictly prohibited by policy.`,
      operatingEntity: market.operatingEntity
    };
  }

  // 2. Submit new Data Subject Rights Request (DSAR)
  public submitDSAR(
    email: string,
    rightType: PrivacyRightsRequest['rightType'],
    jurisdiction: string
  ): PrivacyRightsRequest {
    const trackingCode = `YP-DSAR-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;
    const deadline = new Date();
    deadline.setDate(deadline.getDate() + 30); // 30-day statutory SLA

    const newRequest: PrivacyRightsRequest = {
      id: `dsar-${Date.now()}`,
      trackingCode,
      requesterEmail: email,
      rightType,
      jurisdiction,
      receivedAt: new Date().toISOString(),
      verificationMethod: 'AUTHENTICATED_SESSION',
      verificationStatus: 'VERIFIED',
      statutoryDeadline: deadline.toISOString(),
      status: 'RECEIVED',
      assignedTo: 'Data Protection Officer'
    };

    this.dsarRequests.unshift(newRequest);
    return newRequest;
  }

  public getAllDSARs(): PrivacyRightsRequest[] {
    return this.dsarRequests;
  }

  // 3. Financial Regulatory Kill Switch Evaluator
  public isFinancialFeaturePermitted(killSwitchCode: string): boolean {
    const activity = this.finActivities.find((a) => a.killSwitchCode === killSwitchCode);
    if (!activity) return false;
    return activity.approvedByLegal && activity.regulatoryStatus !== 'PROHIBITED_CURRENT_MODEL';
  }

  // 4. Record Retention Simulator (Dry Run)
  public executeRetentionDryRun(): {
    scannedPoliciesCount: number;
    eligibleForDeletionCount: number;
    protectedByLegalHoldCount: number;
    actionsSummary: string[];
  } {
    let eligible = 0;
    let held = 0;
    const actions: string[] = [];

    this.retentionPolicies.forEach((policy) => {
      if (policy.legalHoldActiveCount > 0) {
        held++;
        actions.push(`[HOLD PROTECTED] Policy "${policy.recordCategory}" has 1 active legal hold (${policy.jurisdiction}). Automatic purge paused.`);
      } else if (policy.triggerEvent === 'CREATED_AT') {
        eligible += 14;
        actions.push(`[DRY RUN PURGE] Scanned 14 expired ${policy.recordCategory} records exceeding ${policy.retentionPeriod}.`);
      } else {
        actions.push(`[EVALUATED] Policy "${policy.recordCategory}" scanned — 0 records past retention threshold.`);
      }
    });

    return {
      scannedPoliciesCount: this.retentionPolicies.length,
      eligibleForDeletionCount: eligible,
      protectedByLegalHoldCount: held,
      actionsSummary: actions
    };
  }

  // 5. Query Active Legal Documents
  public getDocument(slug: string) {
    return LEGAL_DOCUMENTS.find((d) => d.slug === slug);
  }
}

export const complianceEngine = new ComplianceEngine();
